import { useCallback, useState } from 'react';

import { submitCommendaAiMessageFeedback } from '@commenda/api/commenda-ai/submitCommendaAiMessageFeedback';
import type {
  CommendaAiFeedbackRating,
  CommendaAiMessageFeedbackDetails,
  CommendaAiSavedMessageFeedback,
} from '@commenda/api/commenda-ai/types';

interface CommendaAiMessageFeedbackOptions {
  /** The verdict already saved on the server, so a reload keeps the thumb filled and the modal
   * prefilled; a verdict given in this session always wins over it. */
  initialFeedback?: CommendaAiSavedMessageFeedback | null;
  /** Fires after a rating persists, so the conversation-level feedback cache can revalidate. */
  onRatingSaved?: () => void;
}

/** Rating state for one assistant message. A thumb click only opens the modal; nothing is saved
 * until the reader clicks Submit, and Cancel discards the whole rating. */
export function useCommendaAiMessageFeedback(
  conversationId: string,
  messageId: string,
  { initialFeedback = null, onRatingSaved }: CommendaAiMessageFeedbackOptions = {}
) {
  const [submittedFeedback, setSubmittedFeedback] =
    useState<CommendaAiSavedMessageFeedback | null>(null);
  const [openModalRating, setOpenModalRating] =
    useState<CommendaAiFeedbackRating | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const sendFeedback = useCallback(
    async (
      rating: CommendaAiFeedbackRating,
      details: Readonly<CommendaAiMessageFeedbackDetails>
    ): Promise<boolean> => {
      const feedbackBeforeSubmit = submittedFeedback;

      setSubmittedFeedback({
        rating,
        category: details.category ?? null,
        comment: details.comment ?? null,
      });
      setIsSubmittingFeedback(true);
      setFeedbackError(null);

      const result = await submitCommendaAiMessageFeedback({
        conversationId,
        messageId,
        rating,
        ...details,
      });

      setIsSubmittingFeedback(false);

      if (!result.success) {
        setSubmittedFeedback(feedbackBeforeSubmit);
        setFeedbackError(result.error ?? 'Could not save your feedback');
        return false;
      }

      onRatingSaved?.();

      return true;
    },
    [conversationId, messageId, submittedFeedback, onRatingSaved]
  );

  const rateMessage = useCallback((rating: CommendaAiFeedbackRating) => {
    setOpenModalRating(rating);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setOpenModalRating(null);
    setFeedbackError(null);
  }, []);

  const submitFeedbackDetails = useCallback(
    async (details: Readonly<CommendaAiMessageFeedbackDetails>) => {
      if (!openModalRating) return;

      const didSubmit = await sendFeedback(openModalRating, details);

      if (didSubmit) setOpenModalRating(null);
    },
    [openModalRating, sendFeedback]
  );

  // The verdict to prefill the modal from: this session's submit wins, else the server's.
  const savedFeedback = submittedFeedback ?? initialFeedback;

  return {
    /** What the thumbs show: the open modal's tentative pick, else this session's saved rating,
     * else the one loaded from the server. */
    displayedRating:
      openModalRating ??
      submittedFeedback?.rating ??
      initialFeedback?.rating ??
      null,
    savedFeedback,
    openModalRating,
    isSubmittingFeedback,
    feedbackError,
    rateMessage,
    closeFeedbackModal,
    submitFeedbackDetails,
  };
}
